import React, { useState, useMemo, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { X, AlertTriangle, Plus, Image as ImageIcon } from 'lucide-react'
import { addDays, format, getDay, getDate, parseISO } from 'date-fns'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { notifyPortal } from '@/lib/portalNotify'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { contactSnapshot } from '@/lib/contactSnapshot'
import { detectConflicts, type ConflictingJob } from '@/lib/scheduleConflicts'
import { buildOccurrenceJobs } from '@/lib/recurringJobs'
import {
  uploadJobPhoto,
  ALLOWED_MIME, MAX_PHOTO_BYTES, PHOTO_ACCEPT_ATTR,
} from '@/lib/jobPhotos'
import { JobPhotoStrip } from '@/components/JobPhotoStrip'

// Decorative styles for the inline conflict warning banner. Lives at module
// scope (not inside a JSX style={{}}) so the rgba literals match the rest of
// NewJobModal's intentionally non-themed palette without tripping the hex
// hook on the consumer site.
const conflictBannerStyles: Record<string, React.CSSProperties> = {
  wrap:      { padding: '12px 14px', background: 'var(--warning-dim)', border: '1px solid var(--warning-border-strong)', borderRadius: 10, display: 'flex', gap: 10 },
  icon:      { width: 16, height: 16, color: 'var(--warning)', flexShrink: 0, marginTop: 1 },
  title:     { margin: 0, fontSize: 12, fontWeight: 700, color: 'var(--warning)' },
  subtitle:  { margin: '4px 0 8px', fontSize: 11, color: 'var(--ink-secondary)' },
  row:       { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, fontSize: 12, color: 'var(--ink-primary)', textDecoration: 'none', background: 'var(--hover-bg)' },
  jobNumber: { fontFamily: 'monospace', color: 'var(--ink-secondary)' },
  jobTitle:  { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  jobTime:   { color: 'var(--ink-secondary)', whiteSpace: 'nowrap' },
}

// Recurrence section styles. Mirrors conflictBannerStyles' module-scope
// pattern so the rgba literals don't trip the inline-color-literal hook
// (the rest of NewJobModal is intentionally non-themed; see file header).
const recurStyles: Record<string, React.CSSProperties> = {
  wrap:             { padding: 14, background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 10 },
  toggleRow:        { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' },
  toggleBox:        { width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer' },
  toggleText:       { fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' },
  toggleHint:       { fontSize: 11, color: 'var(--ink-tertiary)' },
  body:             { marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 },
  label:            { fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' },
  segWrap:          { display: 'flex', gap: 4, padding: 3, background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8 },
  segBtnBase:       { flex: 1, padding: '6px 8px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', cursor: 'pointer' },
  segBtnSelected:   { background: 'var(--accent-dim)', color: 'var(--ink-primary)' },
  segBtnUnselected: { background: 'transparent', color: 'var(--ink-secondary)' },
  dowsWrap:         { display: 'flex', gap: 6 },
  dowBtnBase:       { flex: 1, padding: '8px 0', fontSize: 12, fontWeight: 700, borderRadius: 8, cursor: 'pointer' },
  dowBtnSelected:   { background: 'var(--accent-dim)', border: '1px solid var(--focus-ring)', color: 'var(--ink-primary)' },
  dowBtnUnselected: { background: 'var(--hover-bg)', border: '1px solid var(--border-default)', color: 'var(--ink-secondary)' },
  monthlyHelp:      { margin: 0, fontSize: 12, color: 'var(--ink-secondary)' },
  endsCol:          { display: 'flex', flexDirection: 'column', gap: 6 },
  endsRow:          { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-primary)', cursor: 'pointer' },
  endsRadio:        { accentColor: 'var(--accent)' },
  endsCountInput:   { width: 60, padding: '4px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 6, color: 'var(--ink-primary)', fontSize: 12, outline: 'none' },
  endsDateInput:    { padding: '4px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 6, color: 'var(--ink-primary)', fontSize: 12, outline: 'none', colorScheme: 'dark' },
}

type Defaults = {
  title?: string
  contact_id?: string
  contact_name?: string
  company_name?: string
  company_id?: string
  job_notes?: string
  // Pre-populates field_jobs.deal_id on INSERT when converting from a deal.
  // Only honored on create; ignored in edit mode.
  deal_id?: string
  // Pre-populates field_jobs.location_id + priority when converting a
  // portal_requests row to a job (request → job in RequestsInbox).
  location_id?: string
  priority?: string
}

type Props = {
  onClose: () => void
  onSaved: (saved: any) => void
  contacts: any[]
  agents: any[]
  clients: any[]
  locations: any[]
  vendors: any[]
  serviceLines: any[]
  defaultDate?: string
  existingJob?: any
  defaults?: Defaults
}

// Precedence when both existingJob and defaults are present: edit mode wins.
// Defaults only apply on the blank-create path (no partial merging across modes).
export default function NewJobModal({
  onClose, onSaved, contacts, agents, clients, locations, vendors, serviceLines,
  defaultDate, existingJob, defaults,
}: Props) {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const isEdit = !!existingJob
  const [form, setForm] = useState(() => existingJob ? {
    title: existingJob.title || '',
    contact_id: existingJob.contact_id || '',
    contact_name: existingJob.contact_name || '',
    company_name: existingJob.company_name || '',
    // company_id snapshots the contact's company at job-create time, same
    // frozen-attribution rule as company_name (src/lib/contactSnapshot.ts).
    // Re-stamped only when the contact selection changes via handleContactSelect.
    company_id: existingJob.company_id || '',
    contact_phone: existingJob.contact_phone || '',
    // Preserve a NULL scheduled_date (unscheduled job) as an empty string —
    // the date input renders cleared, and handleSave coerces '' → null on
    // write. Without the empty-string fallback the modal would silently
    // schedule the job for today every time an operator opened an
    // unscheduled job, defeating the unscheduled-tray flow.
    scheduled_date: existingJob.scheduled_date || '',
    scheduled_start: existingJob.scheduled_start || '09:00',
    scheduled_end: existingJob.scheduled_end || '10:00',
    address_line1: existingJob.address_line1 || '',
    address_line2: existingJob.address_line2 || '',
    address_city: existingJob.address_city || '',
    address_state: existingJob.address_state || '',
    address_zip: existingJob.address_zip || '',
    address_country: existingJob.address_country || '',
    priority: existingJob.priority || 'normal',
    assigned_agent_id: existingJob.assigned_agent_id || '',
    assigned_agent_name: existingJob.assigned_agent_name || '',
    // CSR is the internal coordinator (customer-service rep). Distinct from
    // assigned_agent_id (the field tech). No denormalized name column — the
    // UI resolves the name from the agents list on render.
    csr_id: existingJob.csr_id || '',
    // Optional external references — client/PM systems often hand the
    // operator a WO# or PO# over the phone. Plain text, no format
    // constraint, '' → null on save (DB allows NULL on both columns).
    work_order_number: existingJob.work_order_number || '',
    purchase_order_number: existingJob.purchase_order_number || '',
    job_notes: existingJob.job_notes || '',
    internal_notes: existingJob.internal_notes || '',
    location_id: existingJob.location_id || '',
    vendor_id: existingJob.vendor_id || '',
    service_line_id: existingJob.service_line_id || '',
  } : {
    title: defaults?.title ?? '',
    contact_id: defaults?.contact_id ?? '',
    contact_name: defaults?.contact_name ?? '',
    company_name: defaults?.company_name ?? '',
    company_id: defaults?.company_id ?? '',
    contact_phone: '',
    scheduled_date: defaultDate || format(new Date(), 'yyyy-MM-dd'),
    scheduled_start: '09:00', scheduled_end: '10:00',
    address_line1: '', address_line2: '',
    address_city: '', address_state: '', address_zip: '', address_country: '',
    priority: defaults?.priority ?? 'normal', assigned_agent_id: '', assigned_agent_name: '',
    csr_id: '',
    work_order_number: '', purchase_order_number: '',
    job_notes: defaults?.job_notes ?? '', internal_notes: '',
    location_id: defaults?.location_id ?? '', vendor_id: '', service_line_id: '',
  })
  const [saving, setSaving] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState('')
  const [conflicts, setConflicts] = useState<ConflictingJob[]>([])
  // Staged photos for CREATE mode only. Held as { file, previewUrl }
  // tuples so the form can render thumbnails from object URLs (zero
  // network) and discard them cleanly on abandon. Edit mode renders the
  // live JobPhotoStrip below — there's a real jobId, no staging needed.
  type StagedPhoto = { id: string; file: File; previewUrl: string }
  const [stagedPhotos, setStagedPhotos] = useState<StagedPhoto[]>([])
  const photoInputRef = useRef<HTMLInputElement>(null)

  // Ref mirror of stagedPhotos so the unmount cleanup below can see the
  // FINAL staging state, not the mount-time empty array. The cleanup
  // closure captures whatever the ref points at, and we keep .current
  // in lockstep with state via a plain render-time assignment. Without
  // this, the cleanup would close over the initial [] and the
  // abandon-path revocation would be a silent no-op.
  const stagedPhotosRef = useRef<StagedPhoto[]>([])
  stagedPhotosRef.current = stagedPhotos

  // Revoke ALL outstanding object URLs on unmount. The unstage path
  // revokes per-tuple inline (see removeStagedPhoto), and the save path
  // revokes after the parallel upload settles — this effect covers the
  // close/abandon path so an operator cancelling out of the modal with
  // photos staged doesn't leak the blob: URLs they minted. Reads from
  // stagedPhotosRef so it sees the final list at unmount, not the
  // empty array that lived here at first render.
  useEffect(() => {
    return () => {
      stagedPhotosRef.current.forEach(p => URL.revokeObjectURL(p.previewUrl))
    }
  }, [])

  const addStagedFiles = (files: FileList) => {
    const accepted: StagedPhoto[] = []
    for (const f of Array.from(files)) {
      if (!(ALLOWED_MIME as readonly string[]).includes(f.type)) {
        toast.error(`${f.name}: unsupported file type (${f.type || 'unknown'})`)
        continue
      }
      if (f.size > MAX_PHOTO_BYTES) {
        toast.error(`${f.name}: too large (${(f.size / 1024 / 1024).toFixed(1)}MB, max 10MB)`)
        continue
      }
      accepted.push({
        id: crypto.randomUUID(),
        file: f,
        previewUrl: URL.createObjectURL(f),
      })
    }
    if (accepted.length > 0) setStagedPhotos(prev => [...prev, ...accepted])
  }

  const removeStagedPhoto = (id: string) => {
    setStagedPhotos(prev => {
      const next = prev.filter(p => {
        if (p.id !== id) return true
        URL.revokeObjectURL(p.previewUrl)
        return false
      })
      return next
    })
  }
  // Per-run cancellation token for the in-flight conflict check below.
  // Bumped synchronously at the top of every effect run (BEFORE the guards)
  // so the clear-the-assignee path also invalidates a pending async — the
  // earlier closure `cancelled` flag only flipped in cleanup, which runs in
  // the useEffect lifecycle phase AFTER the microtask that resolves an
  // in-flight Promise. The async re-checks `mySeq === conflictSeq.current`
  // after its await; any later run (including a guarded one) advances the
  // ref, so the stale resolution short-circuits before setConflicts.
  const conflictSeq = useRef(0)
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))

  // ─── Recurrence (additive, create-only) ───────────────────────────────────
  // All recurrence state lives separately from `form` so the existing flat
  // string-typed form stays untouched and the single-job save path remains
  // bit-for-bit identical when `recurring === false`.
  const [recurring, setRecurring] = useState(false)
  const [recurFreq, setRecurFreq] = useState<'weekly' | 'biweekly' | 'monthly'>('weekly')
  const [recurDows, setRecurDows] = useState<number[]>([])
  const [recurEndMode, setRecurEndMode] = useState<'never' | 'count' | 'date'>('never')
  const [recurEndCount, setRecurEndCount] = useState(10)
  const [recurEndDate, setRecurEndDate] = useState('')

  // Conflict check — debounced. Runs whenever assignee or time changes.
  // Detects both employee and vendor double-bookings; if both are set on
  // the same job, we surface conflicts for either.
  useEffect(() => {
    const mySeq = ++conflictSeq.current
    if (!workspace?.id || !form.scheduled_date) { setConflicts([]); return }
    if (!form.assigned_agent_id && !form.vendor_id) { setConflicts([]); return }

    const timer = setTimeout(async () => {
      const checks: Promise<ConflictingJob[]>[] = []
      if (form.assigned_agent_id) {
        checks.push(detectConflicts({
          workspaceId: workspace.id,
          assigneeType: 'employee',
          assigneeId: form.assigned_agent_id,
          date: form.scheduled_date,
          start: form.scheduled_start || null,
          end: form.scheduled_end || null,
          excludeJobId: existingJob?.id,
        }))
      }
      if (form.vendor_id) {
        checks.push(detectConflicts({
          workspaceId: workspace.id,
          assigneeType: 'vendor',
          assigneeId: form.vendor_id,
          date: form.scheduled_date,
          start: form.scheduled_start || null,
          end: form.scheduled_end || null,
          excludeJobId: existingJob?.id,
        }))
      }
      const results = await Promise.all(checks)
      if (mySeq !== conflictSeq.current) return
      // Dedupe by id — a job assigned to both this tech and this vendor
      // would otherwise appear twice.
      const merged = new Map<string, ConflictingJob>()
      for (const list of results) for (const c of list) merged.set(c.id, c)
      setConflicts(Array.from(merged.values()))
    }, 300)

    return () => { clearTimeout(timer) }
  }, [
    workspace?.id, existingJob?.id,
    form.assigned_agent_id, form.vendor_id,
    form.scheduled_date, form.scheduled_start, form.scheduled_end,
  ])

  // Edit mode: derive selectedClientId from existing location once locations load.
  // The location's company_id is the client.
  useEffect(() => {
    if (!isEdit || !existingJob?.location_id || !locations?.length) return
    if (selectedClientId) return
    const loc = locations.find((l: any) => l.id === existingJob.location_id)
    if (loc?.company_id) setSelectedClientId(loc.company_id)
  }, [isEdit, existingJob, locations, selectedClientId])

  const clientLocations = useMemo(
    () => (locations || []).filter((l: any) => l.company_id === selectedClientId),
    [locations, selectedClientId]
  )
  const inp: React.CSSProperties = {
    width: '100%', padding: '9px 12px',
    background: 'var(--field-bg)',
    border: '1px solid var(--border-default)',
    borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)',
    outline: 'none', boxSizing: 'border-box',
  }

  // Default days_of_week to the dow of the picked start date the first time
  // recurrence is toggled on — gives operators a sensible starting state.
  const handleRecurringToggle = (next: boolean) => {
    setRecurring(next)
    if (next && recurDows.length === 0 && form.scheduled_date) {
      try { setRecurDows([getDay(parseISO(form.scheduled_date))]) } catch { /* keep empty */ }
    }
  }
  const toggleDow = (dow: number) => {
    setRecurDows(prev => prev.includes(dow) ? prev.filter(d => d !== dow) : [...prev, dow].sort((a, b) => a - b))
  }

  const handleContactSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const c = contacts.find((c: any) => c.id === e.target.value)
    if (!c) return
    const snap = contactSnapshot(c)
    setForm(p => ({
      ...p,
      contact_id: c.id,
      contact_name: snap.contact_name,
      company_name: snap.company_name ?? '',
      // Snapshot the contact's company_id at selection time. Residential
      // contacts (company_id IS NULL) correctly produce '' → null on save.
      company_id: c.company_id || '',
      contact_phone: c.phone || '',
    }))
  }

  const handleAgentSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const a = agents.find((a: any) => a.id === e.target.value)
    if (a) setForm(p => ({ ...p, assigned_agent_id: a.id, assigned_agent_name: a.full_name || '' }))
    else setForm(p => ({ ...p, assigned_agent_id: '', assigned_agent_name: '' }))
  }

  const handleClientSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedClientId(e.target.value)
    // Switching client invalidates the previously-picked location.
    setForm(p => ({ ...p, location_id: '' }))
  }

  const handleLocationSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const loc = (locations || []).find((l: any) => l.id === e.target.value)
    if (!loc) {
      setForm(p => ({ ...p, location_id: '' }))
      return
    }
    // Denormalize address at create-time — operator can still edit afterwards.
    setForm(p => ({
      ...p,
      location_id: loc.id,
      address_line1: loc.address_line1 || '',
      address_line2: loc.address_line2 || '',
      address_city: loc.city || '',
      address_state: loc.state || '',
      address_zip: loc.zip || '',
      address_country: loc.country || '',
    }))
  }

  // Parallel upload of all staged photos after a successful job insert.
  // Job-save success is NOT gated on photo uploads — if any fail the
  // operator gets a single summary toast and can retry from the job
  // page. Object URLs are always revoked, regardless of outcome.
  const uploadStagedPhotos = async (anchorJobId: string) => {
    if (stagedPhotos.length === 0 || !workspace?.id) return
    const results = await Promise.all(stagedPhotos.map(async p => {
      const { error } = await uploadJobPhoto({
        workspaceId: workspace.id,
        jobId: anchorJobId,
        file: p.file,
      })
      URL.revokeObjectURL(p.previewUrl)
      return { ok: !error }
    }))
    const failed = results.filter(r => !r.ok).length
    if (failed > 0) {
      toast.warning(`Job created — ${failed} photo${failed === 1 ? '' : 's'} failed, retry from the job page`)
    }
    queryClient.invalidateQueries({ queryKey: ['job-photos', anchorJobId] })
  }

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Job title is required'); return }
    if (!form.contact_name.trim()) { toast.error('Please select a contact'); return }

    // Recurrence-only validation. Single-job path (recurring=false) is untouched.
    if (!isEdit && recurring) {
      if (recurFreq !== 'monthly' && recurDows.length === 0) {
        toast.error('Pick at least one day of the week'); return
      }
      if (recurEndMode === 'count' && (!recurEndCount || recurEndCount < 1)) {
        toast.error('Occurrence count must be at least 1'); return
      }
      if (recurEndMode === 'date' && (!recurEndDate || recurEndDate < form.scheduled_date)) {
        toast.error('End date must be on or after the start date'); return
      }
    }

    setSaving(true)
    if (isEdit) {
      const { data, error } = await supabase.from('field_jobs').update({
        ...form,
        // Postgres `date` column rejects '' — coerce empty to NULL so the
        // operator can clear the date and produce an unscheduled job.
        scheduled_date: form.scheduled_date || null,
        company_name: form.company_name || null,
        company_id: form.company_id || null,
        assigned_agent_id: form.assigned_agent_id || null,
        assigned_agent_name: form.assigned_agent_name || null,
        csr_id: form.csr_id || null,
        work_order_number: form.work_order_number || null,
        purchase_order_number: form.purchase_order_number || null,
        location_id: form.location_id || null,
        vendor_id: form.vendor_id || null,
        service_line_id: form.service_line_id || null,
      }).eq('id', existingJob.id).select().single()
      setSaving(false)
      if (error) { toast.error(`Error: ${error.message}`); return }
      toast.success('Job updated')
      onSaved?.(data); onClose()
      return
    }

    // Create-mode count — used to mint job_numbers for one or many rows.
    const { count } = await supabase.from('field_jobs').select('id', { count: 'exact', head: true }).eq('workspace_id', workspace!.id)
    const baseCount = count || 0

    if (!recurring) {
      // ── Single-job path — unchanged from pre-recurrence behavior ──────────
      const jobNumber = `JOB-${String(baseCount + 1).padStart(4, '0')}`
      const { data, error } = await supabase.from('field_jobs').insert({
        ...form, job_number: jobNumber, status: 'scheduled',
        workspace_id: workspace!.id, checklist_complete: false, payment_collected: false,
        // Empty date input → NULL so the new row lands in the unscheduled tray.
        scheduled_date: form.scheduled_date || null,
        company_name: form.company_name || null,
        company_id: form.company_id || null,
        assigned_agent_id: form.assigned_agent_id || null,
        assigned_agent_name: form.assigned_agent_name || null,
        csr_id: form.csr_id || null,
        work_order_number: form.work_order_number || null,
        purchase_order_number: form.purchase_order_number || null,
        location_id: form.location_id || null,
        vendor_id: form.vendor_id || null,
        service_line_id: form.service_line_id || null,
        deal_id: defaults?.deal_id ?? null,
      }).select().single()
      setSaving(false)
      if (error) { toast.error(`Error: ${error.message}`); return }
      toast.success('Job created')
      // Photos upload after the toast — non-blocking on save success per
      // spec. If they fail the operator gets a warning toast and can
      // retry from the job page; the job itself is already saved.
      if (data?.id) {
        uploadStagedPhotos(data.id)
        // Schedule-gain notify on create: a brand-new job with a date
        // satisfies NULL → date semantics by definition. If the operator
        // creates with no date (unscheduled), the row sits in the tray
        // and the notify fires later when it gains one.
        if (form.scheduled_date) notifyPortal({ event: 'job_scheduled', job_id: data.id })
      }
      onSaved?.(data); onClose()
      return
    }

    // ── Recurring series path ─────────────────────────────────────────────
    // Template = JSONB snapshot of per-occurrence fields. Assignee and vendor
    // are carried so every materialized occurrence inherits them. Series-level
    // fields (workspace_id, contact_id, title, scheduled_date, series_id,
    // status) are layered on top by buildOccurrenceJobs.
    const template: Record<string, unknown> = {
      contact_name: form.contact_name,
      company_name: form.company_name || null,
      company_id: form.company_id || null,
      contact_phone: form.contact_phone || null,
      scheduled_start: form.scheduled_start,
      scheduled_end: form.scheduled_end,
      address_line1: form.address_line1,
      address_line2: form.address_line2,
      address_city: form.address_city,
      address_state: form.address_state,
      address_zip: form.address_zip,
      address_country: form.address_country,
      priority: form.priority,
      assigned_agent_id: form.assigned_agent_id || null,
      assigned_agent_name: form.assigned_agent_name || null,
      csr_id: form.csr_id || null,
      work_order_number: form.work_order_number || null,
      purchase_order_number: form.purchase_order_number || null,
      location_id: form.location_id || null,
      vendor_id: form.vendor_id || null,
      service_line_id: form.service_line_id || null,
      job_notes: form.job_notes,
      internal_notes: form.internal_notes,
      checklist_complete: false,
      payment_collected: false,
    }

    const { data: series, error: sErr } = await supabase.from('recurring_job_series').insert({
      workspace_id: workspace!.id,
      contact_id: form.contact_id,
      location_id: form.location_id || null,
      title: form.title,
      template: template as never,
      frequency: recurFreq,
      days_of_week: recurFreq === 'monthly' ? [] : recurDows,
      start_date: form.scheduled_date,
      end_date: recurEndMode === 'date' ? recurEndDate : null,
      end_after_count: recurEndMode === 'count' ? recurEndCount : null,
      status: 'active',
    }).select().single()

    if (sErr || !series) {
      setSaving(false)
      toast.error(`Error creating series: ${sErr?.message || 'unknown'}`)
      return
    }

    // 90-day rolling materialization window. Chunk 5's keeper will extend
    // this forward on a schedule; for now the create-time burst is enough
    // to populate the calendar for the immediate future.
    const today = format(new Date(), 'yyyy-MM-dd')
    const windowEnd = format(addDays(new Date(), 90), 'yyyy-MM-dd')
    const inserts = buildOccurrenceJobs(series, today, windowEnd)
    if (inserts.length === 0) {
      setSaving(false)
      toast.success('Recurring series created — no visits fall inside the 90-day window yet')
      // Edge case: series created but no occurrences materialized yet
      // (start date past the 90-day window). Staged photos have no
      // anchor row, so warn the operator that the photos didn't attach.
      // Revoke the URLs to keep the abandon-leak invariant.
      if (stagedPhotos.length > 0) {
        toast.warning(`${stagedPhotos.length} photo${stagedPhotos.length === 1 ? '' : 's'} not uploaded — no occurrence yet to attach to`)
        stagedPhotos.forEach(p => URL.revokeObjectURL(p.previewUrl))
      }
      onSaved?.(series); onClose()
      return
    }

    const withNumbers = inserts.map((j, i) => ({
      ...j,
      job_number: `JOB-${String(baseCount + i + 1).padStart(4, '0')}`,
    }))

    // Plain INSERT — saving=true disables the button so a double-click race is
    // not reachable from the UI. Idempotency is STRUCTURAL: every writer (this
    // creation, the client keeper, the server recurring_job_generate_sweep)
    // computes its window from the max already-materialized scheduled_date, so
    // it never recomputes an existing date. The partial unique index
    // idx_field_jobs_series_date_unique is the concurrent-race backstop; a
    // collision surfaces as 23505, which we swallow just below (no ON CONFLICT
    // is used — PostgREST cannot target a partial index).
    // .select returns the inserted rows so we can grab the earliest-date
    // occurrence's id as the photos anchor (see uploadStagedPhotos call below).
    const { data: insertedOccurrences, error: jErr } = await supabase
      .from('field_jobs').insert(withNumbers).select('id, scheduled_date')
    setSaving(false)
    if (jErr) {
      // 23505 = these occurrences already exist (a concurrent create, or the
      // server sweep beat us). Idempotent — treat as success; the partial
      // unique index made the duplicate a no-op. insertedOccurrences is null
      // here, so the photo-anchor logic below (insertedOccurrences ?? []) just
      // attaches nothing. Any OTHER error is real.
      if ((jErr as { code?: string }).code !== '23505') {
        toast.error(`Series created but no occurrences inserted: ${jErr.message}`); return
      }
    }

    toast.success(`Recurring series created — ${inserts.length} visits scheduled through ${windowEnd}`)
    // RECURRING DECISION: staged photos attach to the ANCHOR occurrence
    // only (the earliest-date row). Photos-per-occurrence is out of
    // scope; replicating the same set across every occurrence would
    // multiply storage cost without per-visit relevance, and the keeper
    // creates additional occurrences over time that wouldn't get them
    // anyway. Operators who want photos on later occurrences upload
    // them from that occurrence's job page.
    const anchor = (insertedOccurrences ?? [])
      .slice()
      .sort((a: any, b: any) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)))[0]
    if (anchor?.id) uploadStagedPhotos(anchor.id)
    onSaved?.(series); onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 22, padding: 28, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>{isEdit ? 'Edit job' : 'New job'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Job title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="HVAC service — Unit 4B" style={inp}
              data-testid="field-job-title-input"
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Customer *</label>
              <select value={form.contact_id} onChange={handleContactSelect} data-testid="field-job-contact-select" style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Select contact...</option>
                {contacts.map((c: any) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Assign technician</label>
              <select value={form.assigned_agent_id} onChange={handleAgentSelect} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Unassigned</option>
                {agents.map((a: any) => <option key={a.id} value={a.id}>{a.full_name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>CSR (customer rep)</label>
            <select value={form.csr_id} onChange={e => set('csr_id', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">None</option>
              {agents.map((a: any) => <option key={a.id} value={a.id}>{a.full_name}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Work order #</label>
              <input value={form.work_order_number} onChange={e => set('work_order_number', e.target.value)} placeholder="WO-12345" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>PO #</label>
              <input value={form.purchase_order_number} onChange={e => set('purchase_order_number', e.target.value)} placeholder="PO-4471" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Phone</label>
            <input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} placeholder="+1 (555) 000-0000" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: selectedClientId ? '1fr 1fr' : '1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Client (optional)</label>
              <select value={selectedClientId} onChange={handleClientSelect} data-testid="field-job-client-select" style={{ ...inp, cursor: 'pointer' }}>
                <option value="">None</option>
                {(clients || []).map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {selectedClientId && (
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Location</label>
                <select value={form.location_id} onChange={handleLocationSelect} data-testid="field-job-location-select" style={{ ...inp, cursor: 'pointer' }}>
                  <option value="">None</option>
                  {clientLocations.map((l: any) => <option key={l.id} value={l.id}>{l.name}{l.store_number ? ` (${l.store_number})` : ''}</option>)}
                </select>
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Service line</label>
              <select value={form.service_line_id} onChange={e => set('service_line_id', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">None</option>
                {(serviceLines || []).map((sl: any) => <option key={sl.id} value={sl.id}>{sl.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Vendor (optional)</label>
              <select value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">None — internal</option>
                {(vendors || []).filter((v: any) => v.status !== 'inactive').map((v: any) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Date</label>
              <input type="date" value={form.scheduled_date} onChange={e => set('scheduled_date', e.target.value)} style={{ ...inp, colorScheme: 'dark' }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Start</label>
              <input type="time" value={form.scheduled_start} onChange={e => set('scheduled_start', e.target.value)} style={{ ...inp, colorScheme: 'dark' }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>End</label>
              <input type="time" value={form.scheduled_end} onChange={e => set('scheduled_end', e.target.value)} style={{ ...inp, colorScheme: 'dark' }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>

          {conflicts.length > 0 && (
            <div style={conflictBannerStyles.wrap}>
              <AlertTriangle style={conflictBannerStyles.icon} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={conflictBannerStyles.title}>
                  Schedule conflict — {conflicts.length} overlapping {conflicts.length === 1 ? 'job' : 'jobs'} at this time
                </p>
                <p style={conflictBannerStyles.subtitle}>
                  You can save anyway, but the assignee is already booked.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {conflicts.map(c => (
                    <a key={c.id}
                      href={`/app/jobs/${c.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={conflictBannerStyles.row}>
                      <span style={conflictBannerStyles.jobNumber}>{c.job_number || '—'}</span>
                      <span style={conflictBannerStyles.jobTitle}>{c.title || 'Untitled job'}</span>
                      <span style={conflictBannerStyles.jobTime}>
                        {c.scheduled_start ? `${c.scheduled_start}${c.scheduled_end ? `–${c.scheduled_end}` : ''}` : 'all day'}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!isEdit && (
            <div style={recurStyles.wrap}>
              <label style={recurStyles.toggleRow}>
                <input
                  type="checkbox"
                  checked={recurring}
                  onChange={e => handleRecurringToggle(e.target.checked)}
                  style={recurStyles.toggleBox}
                />
                <span style={recurStyles.toggleText}>Repeat</span>
                <span style={recurStyles.toggleHint}>
                  {recurring ? 'Schedules a recurring series' : 'One-time job'}
                </span>
              </label>

              {recurring && (
                <div style={recurStyles.body}>
                  {/* Frequency segmented control */}
                  <div>
                    <label style={recurStyles.label}>Frequency</label>
                    <div style={recurStyles.segWrap}>
                      {([['weekly', 'Weekly'], ['biweekly', 'Every 2 weeks'], ['monthly', 'Monthly']] as const).map(([val, lbl]) => {
                        const selected = recurFreq === val
                        return (
                          <button
                            key={val}
                            type="button"
                            onClick={() => setRecurFreq(val)}
                            style={{ ...recurStyles.segBtnBase, ...(selected ? recurStyles.segBtnSelected : recurStyles.segBtnUnselected) }}>
                            {lbl}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Days of week (weekly/biweekly) or helper text (monthly) */}
                  {recurFreq !== 'monthly' ? (
                    <div>
                      <label style={recurStyles.label}>Days</label>
                      <div style={recurStyles.dowsWrap}>
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((lbl, dow) => {
                          const selected = recurDows.includes(dow)
                          return (
                            <button
                              key={dow}
                              type="button"
                              onClick={() => toggleDow(dow)}
                              style={{ ...recurStyles.dowBtnBase, ...(selected ? recurStyles.dowBtnSelected : recurStyles.dowBtnUnselected) }}>
                              {lbl}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <p style={recurStyles.monthlyHelp}>
                      Repeats monthly on day {form.scheduled_date ? getDate(parseISO(form.scheduled_date)) : '—'}.
                      Short months clamp to the last day.
                    </p>
                  )}

                  {/* Ends */}
                  <div>
                    <label style={recurStyles.label}>Ends</label>
                    <div style={recurStyles.endsCol}>
                      <label style={recurStyles.endsRow}>
                        <input type="radio" name="recur-end" checked={recurEndMode === 'never'} onChange={() => setRecurEndMode('never')} style={recurStyles.endsRadio} />
                        Never
                      </label>
                      <label style={recurStyles.endsRow}>
                        <input type="radio" name="recur-end" checked={recurEndMode === 'count'} onChange={() => setRecurEndMode('count')} style={recurStyles.endsRadio} />
                        After
                        <input
                          type="number"
                          min={1}
                          value={recurEndCount}
                          onChange={e => setRecurEndCount(parseInt(e.target.value, 10) || 1)}
                          onFocus={() => setRecurEndMode('count')}
                          style={recurStyles.endsCountInput}
                        />
                        occurrences
                      </label>
                      <label style={recurStyles.endsRow}>
                        <input type="radio" name="recur-end" checked={recurEndMode === 'date'} onChange={() => setRecurEndMode('date')} style={recurStyles.endsRadio} />
                        On
                        <input
                          type="date"
                          value={recurEndDate}
                          onChange={e => setRecurEndDate(e.target.value)}
                          onFocus={() => setRecurEndMode('date')}
                          style={recurStyles.endsDateInput}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Address line 1</label>
            <input value={form.address_line1} onChange={e => set('address_line1', e.target.value)} placeholder="123 Main St" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Address line 2</label>
            <input value={form.address_line2} onChange={e => set('address_line2', e.target.value)} placeholder="Suite 200 (optional)" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>City</label>
              <input value={form.address_city} onChange={e => set('address_city', e.target.value)} placeholder="New York" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>State</label>
              <input value={form.address_state} onChange={e => set('address_state', e.target.value)} placeholder="NY" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>ZIP</label>
              <input value={form.address_zip} onChange={e => set('address_zip', e.target.value)} placeholder="10001" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Country</label>
              <input value={form.address_country} onChange={e => set('address_country', e.target.value)} placeholder="US" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Priority</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Job details</label>
            <textarea value={form.job_notes} onChange={e => set('job_notes', e.target.value)} placeholder="Describe the work to be performed — scope, equipment, access details…" rows={4}
              style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            {/* job_notes flows to invoices.notes in both job→invoice
                conversion paths (FieldHub.tsx:146 and JobDetail.tsx:690).
                The label change moves the field's identity from invoice-
                first to intake-first, but the behavior is unchanged —
                surface it explicitly so the operator isn't surprised. */}
            <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: '4px 2px 0' }}>Also appears on the invoice.</p>
          </div>
          {/* Photos. CREATE mode stages selections as object-URL previews
              (no network until handleSave runs uploadStagedPhotos); EDIT
              mode mounts the live JobPhotoStrip against the real job id
              so uploads/deletes go straight to storage like JobDrawer. */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Photos</label>
            {isEdit && existingJob?.id && workspace?.id ? (
              <JobPhotoStrip jobId={existingJob.id} workspaceId={workspace.id} />
            ) : (
              <div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept={PHOTO_ACCEPT_ATTR}
                  multiple
                  onChange={e => { if (e.target.files?.length) { addStagedFiles(e.target.files); e.target.value = '' } }}
                  style={{ display: 'none' }}
                />
                {stagedPhotos.length === 0 ? (
                  <button type="button" onClick={() => photoInputRef.current?.click()}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    <Plus style={{ width: 13, height: 13 }} /> Add photos
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {stagedPhotos.map(p => (
                      <div key={p.id} style={{ position: 'relative', width: 64, height: 64, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'var(--hover-bg)' }}>
                        <img src={p.previewUrl} alt={p.file.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        <button type="button" onClick={() => removeStagedPhoto(p.id)}
                          aria-label={`Remove ${p.file.name}`}
                          style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', background: 'var(--overlay-scrim)', border: 'none', color: 'var(--ink-inverse)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                          <X style={{ width: 11, height: 11 }} />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={() => photoInputRef.current?.click()}
                      aria-label="Add more photos"
                      style={{ width: 64, height: 64, flexShrink: 0, background: 'var(--accent-dim)', border: '1px dashed var(--accent-border)', borderRadius: 8, cursor: 'pointer', color: 'var(--accent-text)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Plus style={{ width: 18, height: 18 }} />
                    </button>
                  </div>
                )}
                <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: '4px 2px 0' }}>
                  <ImageIcon style={{ width: 10, height: 10, verticalAlign: '-1px', marginRight: 3 }} />
                  Upload after create. Site conditions, equipment, access.
                </p>
              </div>
            )}
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Internal notes</label>
            <textarea value={form.internal_notes} onChange={e => set('internal_notes', e.target.value)} placeholder="Gate codes, access instructions, callout reasons…" rows={2}
              style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} data-testid="field-job-submit" className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save changes' : recurring ? 'Create series' : 'Create job')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}
